import React, { useState } from 'react';
import { Send, Phone, Mail, MapPin, CheckCircle2, MessageCircle } from 'lucide-react';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';
import { brand } from '../mock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
  const { toast } = useToast();
  const [form, setForm] = useState({
    name: '', email: '', phone: '', requirement: 'ecommerce', message: '', agreed: false,
  });
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.agreed) {
      toast({ title: 'Please agree to be contacted', description: 'You must check the consent box to proceed.' });
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/leads`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        requirement: form.requirement,
        message: form.message,
      });
      setSubmitted(true);
      toast({ title: 'Message sent!', description: 'Our team will reach out shortly.' });
      setForm({ name: '', email: '', phone: '', requirement: 'ecommerce', message: '', agreed: false });
      setTimeout(() => setSubmitted(false), 5000);
    } catch (err) {
      toast({ title: 'Something went wrong', description: 'Please try again or WhatsApp us directly.' });
    } finally {
      setSending(false);
    }
  };

  const waLink = `https://wa.me/${brand.whatsapp}?text=${encodeURIComponent(brand.whatsappMessage)}`;
  const input = 'w-full bg-white border border-neutral-200 rounded-xl px-4 py-3 text-sm text-[color:var(--ink)] placeholder:text-neutral-400 focus:outline-none focus:border-[color:var(--brand-green)] focus:ring-2 focus:ring-[color:var(--brand-green)]/20 transition';

  return (
    <section id="contact" className="section bg-[color:var(--brand-green)] relative overflow-hidden">
      <div className="blob" style={{ background: '#FFB629', width: 400, height: 400, top: -100, right: -100, opacity: 0.2 }} />
      <div className="container-x relative z-10">
        <div className="grid lg:grid-cols-2 gap-10 items-start">
          <div className="text-white">
            <div className="text-[color:var(--brand-yellow)] text-xs font-bold tracking-[4px] uppercase">Contact Us</div>
            <h2 className="text-4xl sm:text-5xl font-black mt-3 leading-tight">
              Act Now and <span className="text-[color:var(--brand-yellow)]">Seize Your Success!</span>
            </h2>
            <p className="mt-4 text-white/85 leading-relaxed max-w-md">
              Enter your details to get strategic insights, action plan and pricing options tailored to your business.
            </p>

            <div className="mt-8 space-y-4">
              <a href={`tel:${brand.phoneRaw}`} className="flex items-center gap-4 group">
                <div className="w-12 h-12 rounded-xl bg-white/15 flex items-center justify-center group-hover:bg-white/25 transition">
                  <Phone size={20} />
                </div>
                <div>
                  <div className="text-xs text-white/70 uppercase font-semibold tracking-wide">Call Us</div>
                  <div className="font-bold">{brand.phone}</div>
                </div>
              </a>
              <a
                href={`https://wa.me/${brand.whatsapp}?text=${encodeURIComponent(brand.whatsappMessage)}`}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-4 group"
              >
                <div className="w-12 h-12 rounded-xl bg-white/15 flex items-center justify-center group-hover:bg-white/25 transition">
                  <MessageCircle size={20} />
                </div>
                <div>
                  <div className="text-xs text-white/70 uppercase font-semibold tracking-wide">WhatsApp</div>
                  <div className="font-bold">{brand.whatsappDisplay}</div>
                </div>
              </a>
              <a href={`mailto:${brand.email}`} className="flex items-center gap-4 group">
                <div className="w-12 h-12 rounded-xl bg-white/15 flex items-center justify-center group-hover:bg-white/25 transition">
                  <Mail size={20} />
                </div>
                <div>
                  <div className="text-xs text-white/70 uppercase font-semibold tracking-wide">Email</div>
                  <div className="font-bold">{brand.email}</div>
                </div>
              </a>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-white/15 flex items-center justify-center">
                  <MapPin size={20} />
                </div>
                <div>
                  <div className="text-xs text-white/70 uppercase font-semibold tracking-wide">Head Office</div>
                  <div className="font-bold">{brand.address}</div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-white/15 flex items-center justify-center">
                  <MapPin size={20} />
                </div>
                <div>
                  <div className="text-xs text-white/70 uppercase font-semibold tracking-wide">Branch Office</div>
                  <div className="font-bold">{brand.branchOffice}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-3xl p-8 shadow-2xl">
            {submitted ? (
              <div className="text-center py-16">
                <div className="w-16 h-16 rounded-full bg-[color:var(--brand-green-light)] flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 size={32} className="text-[color:var(--brand-green)]" />
                </div>
                <h3 className="text-2xl font-black text-[color:var(--ink)]">Thank you!</h3>
                <p className="text-[color:var(--ink-soft)] mt-2">Our team will get in touch with you shortly.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <h3 className="text-xl font-bold text-[color:var(--ink)] mb-2">Send us a message</h3>

                <div>
                  <label className="text-xs font-bold text-[color:var(--ink-soft)] uppercase">Business Requirement</label>
                  <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {[
                      { v: 'ecommerce', l: 'List on Ecommerce Marketplaces' },
                      { v: 'social', l: 'Boost on Social Media' },
                      { v: 'website', l: 'Get a Website Built' },
                      { v: 'gmb', l: 'Enhance Google Presence' },
                    ].map((o) => (
                      <button
                        type="button"
                        key={o.v}
                        onClick={() => setForm({ ...form, requirement: o.v })}
                        className={`text-left px-4 py-2.5 rounded-xl border-2 text-xs font-semibold transition ${
                          form.requirement === o.v
                            ? 'border-[color:var(--brand-green)] bg-[color:var(--brand-green-light)] text-[color:var(--brand-green-dark)]'
                            : 'border-neutral-200 text-[color:var(--ink-soft)] hover:border-[color:var(--brand-green)]'
                        }`}
                      >
                        {o.l}
                      </button>
                    ))}
                  </div>
                </div>

                <input required placeholder="Full Name" className={input} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                <input required type="email" placeholder="Email" className={input} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                <input required placeholder="Mobile Number" className={input} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                <textarea rows={3} placeholder="Message" className={input} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />

                <label className="flex items-start gap-2 text-xs text-[color:var(--ink-soft)] leading-relaxed">
                  <input type="checkbox" className="mt-1 accent-[color:var(--brand-green)]" checked={form.agreed} onChange={(e) => setForm({ ...form, agreed: e.target.checked })} />
                  By submitting this form, you grant us permission to contact you via SMS, WhatsApp, Email and other channels.
                </label>

                <button type="submit" disabled={sending} className="btn-primary w-full justify-center disabled:opacity-60">
                  {sending ? 'Sending...' : 'Send Message'} <Send size={16} />
                </button>

                <a
                  href={waLink}
                  target="_blank"
                  rel="noreferrer"
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-full border-2 border-[#25D366] text-[#25D366] font-semibold text-sm hover:bg-[#25D366] hover:text-white transition"
                >
                  <MessageCircle size={16} /> Chat on WhatsApp
                </a>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
